import { FormikProps } from 'formik'
import React, { forwardRef, useEffect, useState } from 'react'

import { AutoSaveForm, Form } from 'widgets/Form'

import { useFileSelect } from 'hooks/useFileSelect'
import { useMultipleFilesSelect } from 'hooks/useMultipleFilesSelect'

import { AllowedFileType, SelectedFile } from 'types/file'
import { Media } from 'types/media'
import { InProgressPostStep } from 'types/post'

import { MultipleMediaSelector } from './MultipleMediaSelector'

interface Props<Values> {
  allowedFileType: AllowedFileType
  children: (mediaSelector: JSX.Element, props: FormikProps<Values>) => JSX.Element
  initialValues: Values
  onValuesChange: (values: Partial<Values>) => void
  submitCopy?: string
  saveMultipleSteps: (stepsData: Partial<InProgressPostStep[]>) => void
  deleteStepAtIndex: (stepIndex: number) => void
  insertStepAtIndex: (stepIndex: number) => void
}

export const BaseMultipleStepForm = forwardRef(
  <Values extends { file?: Media | SelectedFile; isAudioEnabled?: boolean }>(
    {
      allowedFileType,
      children,
      initialValues,
      onValuesChange,
      submitCopy,
      saveMultipleSteps,
      deleteStepAtIndex,
      insertStepAtIndex,
    }: Props<Values>,
    ref: any,
  ) => {
    const audioEnabledSwitches = initialValues.steps.map((step: InProgressPostStep) => {
      return step?.isAudioEnabled ?? false
    })

    const [isAudioEnabledSwitches, setIsAudioEnabledSwitches] = useState(audioEnabledSwitches)
    const [selectedFileIndex, setSelectedFileIndex] = useState(0)

    useEffect(() => {
      setIsAudioEnabledSwitches(
        initialValues.steps.map((step: InProgressPostStep) => {
          return step?.isAudioEnabled ?? false
        }),
      )
    }, [initialValues.steps.length])

    const files = initialValues.steps.map((step: InProgressPostStep) => {
      return step.file
    })

    const { handleFilesSelect, selectedFiles, selectedFileTypes, selectedFileUrls, handleReset } =
      useMultipleFilesSelect(files)

    const { handleFileSelect, selectedFile, selectedFileType, selectedFileUrl } =
      useFileSelect(undefined)

    const toggleIsAudioEnabled = (index: number) => {
      const allAudioSwitches = isAudioEnabledSwitches
      allAudioSwitches[index] = !allAudioSwitches[index]
      setIsAudioEnabledSwitches(allAudioSwitches)
      saveSteps()
    }

    const saveSteps = () => {
      if (!selectedFiles) return

      const postSteps = selectedFiles.map((file, index) => {
        return {
          ...initialValues.steps[index],
          file,
          isAudioEnabled: isAudioEnabledSwitches[index],
        } as Partial<InProgressPostStep>
      })
      saveMultipleSteps(postSteps)
    }

    const updateStep = () => {
      if (
        !selectedFiles ||
        !selectedFileUrls ||
        !selectedFileTypes ||
        !selectedFile ||
        !selectedFileUrl ||
        !selectedFileType
      ) {
        return
      }

      if (selectedFileIndex === selectedFiles.length) {
        selectedFiles.push(selectedFile)
        selectedFileUrls.push(selectedFileUrl)
        selectedFileTypes.push(selectedFileType)
      } else {
        selectedFiles[selectedFileIndex] = selectedFile
        selectedFileUrls[selectedFileIndex] = selectedFileUrl
        selectedFileTypes[selectedFileIndex] = selectedFileType
      }

      const postSteps = selectedFiles.map((file, index) => {
        return {
          ...initialValues.steps[index],
          file,
          isAudioEnabled: isAudioEnabledSwitches[index],
        } as Partial<InProgressPostStep>
      })
      saveMultipleSteps(postSteps)
    }

    useEffect(() => {
      saveSteps()
    }, [selectedFiles, isAudioEnabledSwitches])

    useEffect(() => {
      updateStep()
    }, [selectedFile])

    const mediaSelector = (
      <MultipleMediaSelector
        ref={ref}
        initialValues={initialValues}
        onValuesChange={onValuesChange}
        allowedFileType={allowedFileType}
        setFiles={handleFilesSelect}
        setFile={handleFileSelect}
        setFileIndex={setSelectedFileIndex}
        toggleIsAudioEnabled={toggleIsAudioEnabled}
        isAudioEnabledSwitches={isAudioEnabledSwitches}
        insertStepAtIndex={insertStepAtIndex}
        deleteStepAtIndex={(index: number) => {
          deleteStepAtIndex(index)
          selectedFiles?.length === 1 && handleReset()
        }}
      />
    )

    return (
      <Form<Values>
        initialValues={{
          ...initialValues,
        }}
        onSubmit={() => {}}
        submitCopy={submitCopy}
      >
        {(props) => (
          <>
            {children(mediaSelector, props)}
            <AutoSaveForm onSave={onValuesChange} debounce={1000} />
          </>
        )}
      </Form>
    )
  },
)
